import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Cpu, CloudUpload, ShieldCheck, ArrowRight } from 'lucide-react';
import { APPLIANCE_BASE_PRICE_USD, quoteLicense } from '@scada-ink/shared/cloud';
import { Button, Container, Reveal } from '@/components/ui';
import { CtaBand, PageHero } from '@/components/sections';
import { FeaturePicker, QuoteSummary, formatUsd, selectionFromParams, selectionToParams, type LicenseSelection } from '@/components/LicenseBuilder';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { useSession } from '@/lib/session';
import { orderLink } from '@/lib/site';

export function LicenseBuilderPage() {
  usePageTitle('License Builder');
  const navigate = useNavigate();
  const { session } = useSession();
  const [selection, setSelection] = useState<LicenseSelection>(() => {
    const fromUrl = selectionFromParams(new URLSearchParams(window.location.search));
    return fromUrl.edition === 'hmi' || fromUrl.all || fromUrl.features.length ? fromUrl : { edition: 'scada', all: true, features: [], maintenance: true };
  });

  // A new system includes the appliance; licenses for an appliance already on
  // site are software only. Online purchase needs a hardware ID, so a new
  // system goes to a quote instead.
  const [newSystem, setNewSystem] = useState(() => new URLSearchParams(window.location.search).get('appliance') !== 'existing');
  const discountPct = session?.account.partnerDiscountPct ?? 0;
  const quote = useMemo(
    () => quoteLicense({ edition: selection.edition, features: selection.features, all: selection.all, discountPct, maintenance: selection.maintenance }),
    [selection, discountPct],
  );

  const continueToPurchase = () => {
    const target = `/portal/licenses/new?${selectionToParams(selection).toString()}`;
    navigate(session ? target : `/register?next=${encodeURIComponent(target)}`);
  };
  const requestSystem = () => navigate(orderLink('quote', Object.fromEntries(selectionToParams(selection, { from: 'builder' }))));
  const scada = selection.edition === 'scada';
  const forNewSystem = scada && newSystem;

  return (
    <>
      <PageHero
        crumbs={[{ label: 'Pricing', to: '/pricing' }, { label: 'License Builder' }]}
        eyebrow="License Builder"
        title="Pay for the features you use — or get them all"
        intro={`A new system starts at ${formatUsd(APPLIANCE_BASE_PRICE_USD)} — the Scada.Ink appliance with the full core platform. Add the capabilities your plant needs and see the price as you go. Already have an appliance? Add features to it with a perpetual license key.`}
      />

      <section className="py-12 lg:py-16">
        <Container>
          <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
            <FeaturePicker selection={selection} onChange={setSelection} />
            <aside className="lg:sticky lg:top-32 space-y-3">
              {scada && (
                <div className="grid grid-cols-2 gap-1 rounded-[var(--radius-glass-sm)] border border-glass-border bg-white p-1" role="radiogroup" aria-label="Appliance">
                  {[
                    { value: true, label: 'New system' },
                    { value: false, label: 'I have an appliance' },
                  ].map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      role="radio"
                      aria-checked={newSystem === o.value}
                      onClick={() => setNewSystem(o.value)}
                      className={`rounded-md px-3 py-2 text-xs font-semibold transition-colors ${
                        newSystem === o.value ? 'bg-brand-primary text-white' : 'text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
              <QuoteSummary
                quote={quote}
                selection={selection}
                baseUsd={forNewSystem ? APPLIANCE_BASE_PRICE_USD : 0}
                onMaintenanceChange={(m) => setSelection({ ...selection, maintenance: m })}
              >
                {forNewSystem ? (
                  <>
                    <Button size="lg" className="w-full" onClick={requestSystem} arrow>
                      Request this system
                    </Button>
                    <p className="mt-3 text-xs text-text-muted text-center">
                      We confirm the hardware and send an invoice — usually within one business day.
                    </p>
                  </>
                ) : (
                  <>
                    <Button size="lg" className="w-full" disabled={quote.empty} onClick={continueToPurchase} arrow>
                      {session ? 'Continue to purchase' : 'Create account to purchase'}
                    </Button>
                    <p className="mt-3 text-xs text-text-muted text-center">
                      You’ll enter your appliance’s hardware ID next.{' '}
                      <button type="button" onClick={() => navigate(orderLink('quote'))} className="text-brand-primary font-semibold">
                        Prefer a quote?
                      </button>
                    </p>
                  </>
                )}
              </QuoteSummary>
            </aside>
          </div>
        </Container>
      </section>

      <section className="py-16 bg-white border-y border-glass-border">
        <Container>
          <h2 className="text-2xl font-bold tracking-tight mb-8">How licensing works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Cpu, title: 'Find the hardware ID', body: 'On the appliance, open Admin → Settings. The License card shows its hardware ID with a copy button.' },
              { icon: KeyRound, title: 'Buy the key', body: 'Choose features, enter the hardware ID and pay. The key is issued instantly, locked to that appliance.' },
              { icon: ShieldCheck, title: 'Activate offline', body: 'Paste the key into the License card. It is verified on the appliance itself — no internet connection needed.' },
              { icon: CloudUpload, title: 'Backed up for you', body: 'Every key you buy is stored in your Scada.Ink Cloud account, and you can store keys you already have.' },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 70}>
                <div className="glass-card h-full p-5">
                  <div className="flex items-center justify-between">
                    <s.icon size={20} className="text-brand-primary" />
                    <span className="text-2xl font-extrabold text-ink-muted">{i + 1}</span>
                  </div>
                  <h3 className="mt-3 font-semibold">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-text-secondary leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5 text-sm">
            {[
              ['Perpetual', 'Keys never expire. Maintenance adds software updates and priority support each year, and is optional.'],
              ['One appliance per key', 'A key works only on the appliance whose hardware ID it was issued for, and licenses can’t be transferred.'],
              ['Upgrade any time', 'Add features later and pay only for what’s new — upgrading to All credits the features you already own.'],
            ].map(([t, b]) => (
              <div key={t} className="flex gap-3">
                <ArrowRight size={16} className="text-brand-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">{t}</div>
                  <div className="text-text-secondary mt-0.5">{b}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <CtaBand title="Buying for several appliances or a whole site?" body="Tell us how many appliances you are licensing and we will quote volume pricing, hardware and commissioning together." />
    </>
  );
}
